import { useEffect, useState } from 'react';

// Mobile ends at Tailwind's `md` breakpoint, so JS checks and `md:` utilities flip at the same width.
// The MUI theme overrides `md` to 930px, so `theme.breakpoints.down('md')` is not the same thing.
export const MOBILE_BREAKPOINT = 768;
export const WIDE_DESKTOP_BREAKPOINT = 1400;

export const RESIZE_DEBOUNCE_MS = 100;

/**
 * The window's width, settled once resizing stops.
 *
 * Hand-rolled rather than usehooks-ts's `useWindowSize({ debounceDelay })`, which leaks a timer per
 * mount: it calls its debounced setter once on mount and drops only the resize listener on unmount,
 * leaving the pending call scheduled. In the app that is a setState into an unmounted tree; under
 * Vitest the timer lands after jsdom is gone and the run dies on "window is not defined", which is
 * timing-dependent and so fails on CI while passing locally. Clearing the timeout is the difference.
 */
export function useWindowWidth(): number {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setWidth(window.innerWidth), RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return width;
}

export const useScreenSize = () => {
  const width = useWindowWidth();

  return {
    isMobile: width < MOBILE_BREAKPOINT,
    isNarrowDesktop: width >= MOBILE_BREAKPOINT && width < WIDE_DESKTOP_BREAKPOINT,
    isWideDesktop: width >= WIDE_DESKTOP_BREAKPOINT,
  };
};
